import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TableSortLabel,
  TablePagination,
  Button,
  Stack,
  Breadcrumbs,
  Link
} from '@mui/material';
import { TableChart as TableChartIcon, Home as HomeIcon } from '@mui/icons-material';
import { rptService } from '../../../../services/rptService';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const CustomReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState(null);
  const [yearFilter, setYearFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'assessed_value', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [amountPaidByPropertyId, setAmountPaidByPropertyId] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      const res = await rptService.getAllProperties();
      if (res.success) {
        setProperties(res.data || []);
      } else {
        setError(res.error || 'Failed to load properties');
      }
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [yearFilter, sortConfig]);

  const availableYears = useMemo(() => {
    const years = new Set();
    properties.forEach(p => {
      if (p.last_payment_year) years.add(p.last_payment_year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [properties]);

  const filtered = useMemo(() => {
    return properties.filter(p => {
      if (yearFilter === 'all') return true;
      return String(p.last_payment_year || '') === String(yearFilter);
    });
  }, [properties, yearFilter]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    if (sortConfig.key === 'assessed_value') {
      items.sort((a, b) => {
        const avA = parseFloat(a.assessed_value || 0);
        const avB = parseFloat(b.assessed_value || 0);
        if (avA < avB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (avA > avB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [filtered, sortConfig]);

  const handleSortByAssessed = () => {
    setSortConfig(prev => ({
      key: 'assessed_value',
      direction: prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleChangePage = (_e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sorted.slice(start, end);
  }, [sorted, page, rowsPerPage]);

  const getTargetYearForProperty = (p) => {
    if (yearFilter === 'all') return p?.last_payment_year ? Number(p.last_payment_year) : null;
    const y = Number(yearFilter);
    return Number.isFinite(y) ? y : null;
  };

  const getPaymentRatioFromBreakdown = (payment) => {
    const breakdown = payment?.year_breakdown;
    if (!Array.isArray(breakdown) || breakdown.length === 0) return 1;
    const totalDue = breakdown.reduce((sum, row) => {
      const full = Number(row?.fullPayment || 0);
      const discPen = Number((row?.discount || 0) > 0 ? -(row?.discount || 0) : (row?.penalty || 0));
      return sum + full + discPen;
    }, 0);
    if (!totalDue) return 1;
    return Number(payment?.amount_paid || 0) / totalDue;
  };

  const getAllocatedPaymentForYear = (payment, year) => {
    const y = Number(year);
    if (!Number.isFinite(y)) return 0;

    const yFrom = Number(payment?.year_from);
    const yTo = Number(payment?.year_to);
    if (Number.isFinite(yFrom) && Number.isFinite(yTo)) {
      if (y < yFrom || y > yTo) return 0;
    }

    const breakdown = payment?.year_breakdown;
    if (Array.isArray(breakdown) && breakdown.length > 0) {
      const row = breakdown.find(r => Number(r?.year) === y);
      if (row) {
        const full = Number(row?.fullPayment || 0);
        const discPen = Number((row?.discount || 0) > 0 ? -(row?.discount || 0) : (row?.penalty || 0));
        const ratio = getPaymentRatioFromBreakdown(payment);
        return (full + discPen) * ratio;
      }
    }

    if (Number.isFinite(yFrom) && Number.isFinite(yTo)) {
      const yearsCount = yTo - yFrom + 1;
      if (yearsCount > 0) return Number(payment?.amount_paid || 0) / yearsCount;
    }

    return 0;
  };

  const sumPaidForPropertyYear = (payments, year) => {
    if (!Array.isArray(payments) || payments.length === 0) return 0;
    const y = Number(year);
    if (!Number.isFinite(y)) return 0;
    return payments.reduce((sum, pay) => sum + getAllocatedPaymentForYear(pay, y), 0);
  };

  useEffect(() => {
    const loadAmounts = async () => {
      const ids = paged.map(p => p.property_id).filter(Boolean);
      if (ids.length === 0) {
        setAmountPaidByPropertyId({});
        return;
      }

      const y = yearFilter === 'all' ? undefined : Number(yearFilter);
      const res = await rptService.getPaymentsForProperties(ids, { year: Number.isFinite(y) ? y : undefined });
      if (!res.success) {
        setAmountPaidByPropertyId({});
        return;
      }

      const grouped = res.data.reduce((acc, pay) => {
        const id = pay?.property_id;
        if (!id) return acc;
        if (!acc[id]) acc[id] = [];
        acc[id].push(pay);
        return acc;
      }, {});

      const map = {};
      paged.forEach(p => {
        const year = getTargetYearForProperty(p);
        if (!year) return;
        map[p.property_id] = sumPaidForPropertyYear(grouped[p.property_id] || [], year);
      });
      setAmountPaidByPropertyId(map);
    };
    loadAmounts();
  }, [paged, yearFilter]);

  const formatCurrency = (amount) => {
    const n = parseFloat(amount || 0);
    return `₱${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const rowsHtml = paged.map(p => `
      <tr>
        <td style="padding:6px;border:1px solid #e5e7eb">${p.td_number || p.arpn || 'N/A'}</td>
        <td style="padding:6px;border:1px solid #e5e7eb">${p.owner_name || 'Unknown'}</td>
        <td style="padding:6px;border:1px solid #e5e7eb">${p.barangay || 'N/A'}</td>
        <td style="padding:6px;border:1px solid #e5e7eb;text-align:right">₱${parseFloat(p.assessed_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        <td style="padding:6px;border:1px solid #e5e7eb;text-align:center">${getTargetYearForProperty(p) || '—'}</td>
        <td style="padding:6px;border:1px solid #e5e7eb;text-align:right">₱${parseFloat(amountPaidByPropertyId[p.property_id] || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      </tr>
    `).join('');
    printWindow.document.write(`
      <html>
        <head>
          <title>Custom Reports</title>
          <style>
            body{font-family:Arial,Helvetica,sans-serif;color:#111827;margin:24px}
            h1{font-size:18px;margin:0 0 8px 0}
            .sub{color:#6b7280;margin-bottom:16px}
            table{border-collapse:collapse;width:100%}
            thead th{background:#f8fafc;color:#111827;text-align:left;border:1px solid #e5e7eb;padding:8px}
            tbody td{padding:6px}
          </style>
        </head>
        <body>
          <h1>Custom Reports</h1>
          <div class="sub">Generated on ${new Date().toLocaleString()}</div>
          <table>
            <thead>
              <tr>
                <th>Tax Dec. No.</th>
                <th>Owner Name</th>
                <th>Barangay</th>
                <th style="text-align:right">Assessed Value</th>
                <th style="text-align:center">Year Paid</th>
                <th style="text-align:right">Amount Paid</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <script>window.onload=function(){window.print()}</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleExportExcel = async () => {
    const buildAmountMapForExport = async () => {
      const list = sorted.filter(p => p?.property_id);
      if (list.length === 0) return {};

      const y = yearFilter === 'all' ? undefined : Number(yearFilter);
      const batchSize = 100;
      const map = {};

      for (let i = 0; i < list.length; i += batchSize) {
        const batch = list.slice(i, i + batchSize);
        const ids = batch.map(p => p.property_id);
        const res = await rptService.getPaymentsForProperties(ids, { year: Number.isFinite(y) ? y : undefined });
        if (!res.success) continue;

        const grouped = res.data.reduce((acc, pay) => {
          const id = pay?.property_id;
          if (!id) return acc;
          if (!acc[id]) acc[id] = [];
          acc[id].push(pay);
          return acc;
        }, {});

        batch.forEach(p => {
          const targetYear = yearFilter === 'all' ? (p?.last_payment_year ? Number(p.last_payment_year) : null) : (Number.isFinite(y) ? y : null);
          if (!targetYear) {
            map[p.property_id] = 0;
            return;
          }
          map[p.property_id] = sumPaidForPropertyYear(grouped[p.property_id] || [], targetYear);
        });
      }

      return map;
    };

    const paidMap = await buildAmountMapForExport();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Custom Reports');
    worksheet.pageSetup.orientation = 'landscape';
    worksheet.columns = [
      { header: 'Tax Dec. No.', key: 'td', width: 18 },
      { header: 'Owner Name', key: 'owner', width: 28 },
      { header: 'Barangay', key: 'brgy', width: 18 },
      { header: 'Assessed Value', key: 'av', width: 18 },
      { header: 'Year Paid', key: 'year', width: 12 },
      { header: 'Amount Paid', key: 'paid', width: 18 }
    ];
    sorted.forEach(p => {
      const targetYear = getTargetYearForProperty(p);
      worksheet.addRow({
        td: p.td_number || p.arpn || '',
        owner: p.owner_name || '',
        brgy: p.barangay || '',
        av: parseFloat(p.assessed_value || 0),
        year: targetYear || '',
        paid: Number(paidMap[p.property_id] || 0)
      });
    });
    worksheet.getColumn('av').numFmt = '#,##0.00';
    worksheet.getColumn('paid').numFmt = '#,##0.00';
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Custom_Reports_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 8 }}>
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.875rem' }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/portal/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Portal
            </Link>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/portal/property-tax/dashboard')}
            >
              RPT System
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 500 }}>
              Custom Reports
            </Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" sx={{
              fontWeight: 800,
              color: '#1e293b',
              letterSpacing: '-0.02em',
              background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}>
              Custom Reports
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px', lineHeight: 1.6 }}>
              Displays the list of properties with a filter for Year Paid and sorting by Assessed Value.
            </Typography>
          </Box>
        </Box>

        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', overflow: 'visible' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth sx={{ bgcolor: '#f8fafc' }}>
                  <InputLabel>Year Paid</InputLabel>
                  <Select
                    value={yearFilter}
                    label="Year Paid"
                    onChange={(e) => setYearFilter(e.target.value)}
                  >
                    <MenuItem value="all"><em>All Years</em></MenuItem>
                    {availableYears.map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md="auto">
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handlePrint}
                    disabled={loading || sorted.length === 0}
                    sx={{ bgcolor: '#1e293b', '&:hover': { bgcolor: '#334155' } }}
                  >
                    Print
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleExportExcel}
                    disabled={loading || sorted.length === 0}
                    sx={{ bgcolor: '#047857', '&:hover': { bgcolor: '#065f46' } }}
                  >
                    Excel
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  {sorted.length > 0
                    ? `Showing ${page * rowsPerPage + 1}-${Math.min(sorted.length, (page + 1) * rowsPerPage)} of ${sorted.length} records`
                    : 'No records'}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b' }}>
                        Tax Dec. No.
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b' }}>
                        Owner Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b' }}>
                        Barangay
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b' }}>
                        <TableSortLabel
                          active={sortConfig.key === 'assessed_value'}
                          direction={sortConfig.direction}
                          onClick={handleSortByAssessed}
                        >
                          Assessed Value
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b' }}>
                        Year Paid
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#1e293b' }}>
                        Amount Paid
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map(p => (
                      <TableRow key={p.property_id}>
                        <TableCell>{p.td_number || p.arpn || 'N/A'}</TableCell>
                        <TableCell>{p.owner_name || 'Unknown'}</TableCell>
                        <TableCell>{p.barangay || 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                          {formatCurrency(p.assessed_value)}
                        </TableCell>
                        <TableCell align="center">
                          {getTargetYearForProperty(p) || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                          {getTargetYearForProperty(p) ? formatCurrency(amountPaidByPropertyId[p.property_id] || 0) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {sorted.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <TableChartIcon color="disabled" />
                            <Typography variant="body2">No properties match the selected filter</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={sorted.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Card>
      </Container>
    </Box>
  );
};

export default CustomReportsPage;
